import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ContentCopy from '@mui/icons-material/ContentCopy';

import {
  INTEGRATION_STATUS_REFRESH_INTERVAL,
  URL_VALIDATION_REGEX,
} from '@cd3p/core/constants/common';
import {
  APIIntegrationStatus,
  APIProviderIntegrationStatusModel,
  APIProviderSettings,
} from '@cd3p/core/types/api';
import {
  LocalShippingOutlined,
  SignalCellularAltOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormField,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';

import { IntegrationServiceField } from './IntegrationServiceField';
import { IntegrationStatusIcon } from './IntegrationStatusIcon';
import { SyncTrucksPopup } from './SyncTrucksPopup/SyncTrucksPopup';
import {
  IntegrationSetting,
  TicketIntegrationType,
  gpsIntegrations,
  ticketIntegrations,
} from './integrationSettings';

import {
  FormProvider,
  _,
  styled,
  useForm,
  useSelector,
  useTranslation,
} from 'third-party';

import { USER_EDIT_INPUT_DEBOUNCE } from 'constants/common';

import { useApp } from 'modules/app';
import { NotificationSeverity, useNotifications } from 'modules/notifications';
import { useTracking } from 'modules/tracking';

import { appSelectors, trackingSelectors } from 'selectors';

const HeaderContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-top: 2rem;
  gap: 2rem;
`;

const Header = styled(Typography)`
  color: ${props => props.theme.custom.palette.secondary900};
`;

const Wrapper = styled(Stack)`
  align-items: center;
  min-height: 0;
  overflow-y: auto;
`;

const SectionDescription = styled(Typography)`
  font-size: 1.6rem;
  font-weight: 400;
  color: ${props => props.theme.custom.palette.darkText};
  margin-top: 1rem;
`;

const Inner = styled(Grid)`
  flex-grow: 1;
  padding: 0 0.2rem 2rem;
  width: 65rem;
`;

const DividerStyled = styled(Divider)`
  margin-top: 2rem;
  border-color: ${({ theme }) => theme.custom.palette.muted100};
`;

const StyledForm = styled.form`
  padding-bottom: 2rem;
`;

const StyledUrlWrapper = styled(Stack)`
  margin-top: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.palette.background.default};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0.5rem;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const InstructionBlock = styled.div`
  border: 1px solid ${props => props.theme.custom.palette.primary700};
  border-radius: 1rem;
  min-height: 4rem;
  padding: 1rem;
  margin-top: 1rem;

  & a,
  a:visited {
    color: ${props => props.theme.custom.palette.primary800};
  }
`;

const ContentCopyIcon = styled(ContentCopy)`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  hover {
    color: ${({ theme }) => theme.palette.primary.dark};
  }
`;

const ConnectionButtonWrapper = styled(Stack)`
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  flex-direction: row;
  gap: 1.2rem;
`;

const ConnectionButton = styled(Button)`
  width: 'auto';
  margin-right: 1rem;
  font-weight: 900;
  border-width: 0.2rem;
  &:hover {
    border-width: 0.2rem;
  }
`;

const LocalShippingOutlinedIcon = styled(LocalShippingOutlined)`
  margin-right: 0.5rem;
`;

const SignalCellularAltOutlinedIcon = styled(SignalCellularAltOutlined)`
  margin-right: 0.5rem;
`;

enum FormFields {
  GpsProvider = 'GpsProvider',
  TicketsIntegrationService = 'TicketsIntegrationService',
}

type FormData = {
  [FormFields.GpsProvider]: string;
  [FormFields.TicketsIntegrationService]: string;
};

const formDefaultValues = (() => {
  const gpsIntegrationsDefaults = gpsIntegrations
    .flatMap(it => it.settings)
    .reduce<Record<string, string>>((acc, it) => {
      acc[it.name] = it.defaultValue || '';
      return acc;
    }, {});

  const ticketIntegrationsDefaults = ticketIntegrations
    .flatMap(it => it.settings)
    .reduce<Record<string, string>>((acc, it) => {
      if (!it) return acc;

      acc[it.name] = it.defaultValue || '';
      return acc;
    }, {});

  return {
    GpsProvider: '',
    TicketsIntegrationService: '',
    ...gpsIntegrationsDefaults,
    ...ticketIntegrationsDefaults,
  };
})();

type Props = {
  data: Partial<FormData>;
  providerId: string;
};

export const IntegrationSettingsForm: React.FC<Props> = ({
  data,
  providerId,
}) => {
  const { t } = useTranslation();
  const handleApiResult = useHandleApiResult();
  const { updateProviderSettings, deleteProviderSetting } = useApp();
  const { testGpsConnection } = useTracking();
  const { testTicketConnection, fetchProviderIntegrationStatuses } = useApp();
  const { addNotification } = useNotifications();
  const [ticketIntegrationHookUrl, setTicketIntegrationHookUrl] = useState('');
  const [syncTrucksPopupOpen, setSyncTrucksPopupOpen] = useState(false);
  const testGpsConnectionPending = useSelector(
    trackingSelectors.testGpsConnectionPending,
  );
  const syncTrucksPending = useSelector(trackingSelectors.syncTrucksPending);
  const testTicketConnectionPending = useSelector(
    appSelectors.testTicketConnectionPending,
  );
  const [ticketIntegrationStatus, setTicketIntegrationStatus] =
    useState<APIProviderIntegrationStatusModel>();
  const [gpsIntegrationStatus, setGpsIntegrationStatus] =
    useState<APIProviderIntegrationStatusModel>();

  const formMethods = useForm<FormData>({
    defaultValues: {
      ...formDefaultValues,
      ...data,
    },
    mode: 'onChange',
  });

  const {
    watch,
    control,
    getValues,
    setValue,
    trigger,
    clearErrors,
    reset,
    formState: { errors, isValid },
  } = formMethods;

  const watchedGpsProvider = watch(FormFields.GpsProvider);
  const watchedTicketsIntegrationService = watch(
    FormFields.TicketsIntegrationService,
  );

  const selectedGpsIntegration = useMemo(
    () => gpsIntegrations.find(it => it.name === watchedGpsProvider),
    [watchedGpsProvider],
  );

  const selectedTicketIntegration = useMemo(
    () =>
      ticketIntegrations.find(
        it => it.name === watchedTicketsIntegrationService,
      ),
    [watchedTicketsIntegrationService],
  );

  const gpsProviderOptions = useMemo(() => {
    return gpsIntegrations.map(it => (
      <MenuItem
        key={it.name}
        value={it.name}
        selected={it.name === watchedGpsProvider}
      >
        {it.label}
      </MenuItem>
    ));
  }, [watchedGpsProvider]);

  const ticketsIntegrationServiceOptions = useMemo(() => {
    return ticketIntegrations.map(it => (
      <MenuItem
        key={it.name}
        value={it.name}
        selected={it.name === watchedTicketsIntegrationService}
      >
        {it.label}
      </MenuItem>
    ));
  }, [watchedTicketsIntegrationService]);

  useEffect(() => {
    if (selectedTicketIntegration) {
      if (
        selectedTicketIntegration.type === TicketIntegrationType.Polling &&
        selectedTicketIntegration.settings
      ) {
        setTicketIntegrationHookUrl('');
      }

      if (
        selectedTicketIntegration.type === TicketIntegrationType.Webhook &&
        selectedTicketIntegration.webhookUrlTemplate
      ) {
        setTicketIntegrationHookUrl(
          selectedTicketIntegration.webhookUrlTemplate.replace(
            '{providerId}',
            providerId,
          ),
        );
      }
    }
  }, [
    watchedTicketsIntegrationService,
    selectedTicketIntegration,
    setTicketIntegrationHookUrl,
    providerId,
  ]);

  const fetchIntegrationStatuses = useCallback(async () => {
    handleApiResult(
      () => fetchProviderIntegrationStatuses(providerId),
      ({ result }) => {
        const integrationStatuses =
          result.payload as APIProviderIntegrationStatusModel[];

        const gpsIntegrationStatus = integrationStatuses.find(
          it => it.integrationType === 'Gps',
        );
        const ticketIntegrationStatus = integrationStatuses.find(
          it => it.integrationType === 'Ticket',
        );

        setGpsIntegrationStatus(gpsIntegrationStatus);
        setTicketIntegrationStatus(ticketIntegrationStatus);
      },
    );
  }, [handleApiResult, fetchProviderIntegrationStatuses, providerId]);

  useEffect(() => {
    if (!selectedTicketIntegration && !selectedGpsIntegration) {
      return;
    }

    fetchIntegrationStatuses();

    const interval = setInterval(
      fetchIntegrationStatuses,
      INTEGRATION_STATUS_REFRESH_INTERVAL,
    );

    return () => clearInterval(interval);
  }, [
    fetchIntegrationStatuses,
    selectedTicketIntegration,
    selectedGpsIntegration,
  ]);

  const clearIntegrationSetting = async (providerId: string, key: string) => {
    const isValid = await trigger(key as FormFields);

    if (!isValid) {
      setValue(key as FormFields, '');
      clearErrors(key as FormFields);
      return;
    }

    return handleApiResult<APIProviderSettings[]>(
      () => deleteProviderSetting(providerId, key),
      ({ result }) => {
        if (result.payload) {
          setValue(key as FormFields, '');

          fetchIntegrationStatuses();
        } else {
          addNotification({
            severity: NotificationSeverity.Error,
            message: t('common.generalError'),
          });
        }
      },
    );
  };

  const clearIntegrationSettings = async (
    settings: Array<IntegrationSetting>,
  ) => {
    settings
      .filter(it => {
        const value = getValues(it.name as FormFields);

        return value !== formDefaultValues[it.name as FormFields];
      })
      .forEach(it => clearIntegrationSetting(providerId, it.name));
  };

  const onClearTicketIntegration = async () => {
    if (selectedTicketIntegration?.settings) {
      await clearIntegrationSettings(selectedTicketIntegration.settings);
    }

    setTicketIntegrationHookUrl('');

    await clearIntegrationSetting(
      providerId,
      FormFields.TicketsIntegrationService,
    );
  };

  const onClearGpsProvider = async () => {
    if (selectedGpsIntegration?.settings) {
      await clearIntegrationSettings(selectedGpsIntegration.settings);
    }

    await clearIntegrationSetting(providerId, FormFields.GpsProvider);
  };

  const autoSaveField = _.debounce(async (fieldName: string) => {
    const value = getValues(fieldName as FormFields);
    const isValid = await trigger(fieldName as FormFields);

    if (!isValid) {
      return;
    }

    if (value === formDefaultValues[fieldName as FormFields]) {
      await clearIntegrationSetting(providerId, fieldName);
      return;
    }

    if (
      fieldName === FormFields.GpsProvider &&
      selectedGpsIntegration?.settings
    ) {
      clearIntegrationSettings(selectedGpsIntegration?.settings);
    }

    if (
      fieldName === FormFields.TicketsIntegrationService &&
      selectedTicketIntegration?.settings
    ) {
      clearIntegrationSettings(selectedTicketIntegration?.settings);
    }

    await handleApiResult<APIProviderSettings[]>(
      () =>
        updateProviderSettings(providerId, [
          {
            key: fieldName,
            value: value.toString(),
          },
        ]),
      () => {
        fetchIntegrationStatuses();
      },
    );
  }, USER_EDIT_INPUT_DEBOUNCE);

  const onTestTicketConnectionClick = async (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();

    await handleApiResult<APIIntegrationStatus>(
      () => testTicketConnection(providerId),
      ({ showSuccessToast, result }) => {
        if (result.payload.isIntegrationSuccess) {
          showSuccessToast(
            t('settings.integrations.form.testConnectionSuccess'),
          );
        } else {
          addNotification({
            severity: NotificationSeverity.Error,
            message: `${t('settings.integrations.form.testConnectionError')}: ${
              result.payload.message
            }`,
          });
        }
      },
    );
  };

  const onTestGpsConnectionClick = async (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();

    await handleApiResult<APIIntegrationStatus>(
      () => testGpsConnection(providerId),
      ({ showSuccessToast, result }) => {
        if (result.payload.isIntegrationSuccess) {
          showSuccessToast(
            t('settings.integrations.form.testConnectionSuccess'),
          );
        } else {
          addNotification({
            severity: NotificationSeverity.Error,
            message: `${t('settings.integrations.form.testConnectionError')}: ${
              result.payload.message
            }`,
          });
        }
      },
    );
  };

  const onSyncTrucksClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSyncTrucksPopupOpen(true);
  };

  return (
    <Wrapper>
      <Inner>
        <FormProvider {...formMethods}>
          <StyledForm>
            <HeaderContainer>
              <Header variant="subtitle2" display="flex" alignItems="center">
                {t('settings.integrations.form.ticketIntegrationLabel')}
              </Header>

              {ticketIntegrationStatus && (
                <IntegrationStatusIcon
                  integrationStatusModel={ticketIntegrationStatus}
                />
              )}
            </HeaderContainer>

            <SectionDescription>
              {t('settings.integrations.form.ticketIntegrationDescription')}
            </SectionDescription>

            <IntegrationServiceField
              fieldName={FormFields.TicketsIntegrationService}
              label={t('settings.integrations.form.ticketIntegrationLabel')}
              placeholder={t(
                'settings.integrations.form.ticketIntegrationPlaceholder',
              )}
              control={control}
              integrationServiceOptions={ticketsIntegrationServiceOptions}
              isIntegrationSelected={!!selectedTicketIntegration}
              integrationStatus={ticketIntegrationStatus?.status || null}
              onChangeCancel={() => reset()}
              onChangeConfirm={() =>
                autoSaveField(FormFields.TicketsIntegrationService)
              }
              onClearIntegrationConfirm={onClearTicketIntegration}
            />

            {selectedTicketIntegration?.settings?.map(field => (
              <FormField
                isRequired={field.isRequired}
                key={field.name}
                fieldName={field.name}
                fieldError={errors[field.name as FormFields]}
                label={field.label}
                inputProps={{
                  type: field.inputType,
                }}
                placeholder={field.placeholder}
                onChange={() => autoSaveField(field.name)}
                requiredErrorMessage={t('common.form.error.emptyField')}
                control={control}
                rules={{
                  ...(field.inputType === 'url'
                    ? {
                        pattern: {
                          value: URL_VALIDATION_REGEX,
                          message: t('common.form.invalidUrlError'),
                        },
                      }
                    : {}),
                }}
              />
            ))}

            {selectedTicketIntegration?.instructions && (
              <InstructionBlock>
                <Typography fontWeight={600} variant="body1" pb="1rem">
                  {t('settings.integrations.instruction')}
                </Typography>
                <Typography variant="body1">
                  {selectedTicketIntegration?.instructions as string}
                </Typography>
              </InstructionBlock>
            )}

            {selectedTicketIntegration?.type === 'polling' && (
              <ConnectionButtonWrapper>
                <ConnectionButton
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={onTestTicketConnectionClick}
                  loading={testTicketConnectionPending}
                  disabled={!isValid}
                >
                  <SignalCellularAltOutlinedIcon />
                  {t('settings.integrations.form.testConnection')}
                </ConnectionButton>
              </ConnectionButtonWrapper>
            )}

            {ticketIntegrationHookUrl && (
              <>
                <SectionDescription>
                  {t(
                    'settings.integrations.form.integrationHookUrlDescription',
                  )}
                </SectionDescription>
                <StyledUrlWrapper>
                  <Typography variant="body1">
                    {ticketIntegrationHookUrl}
                  </Typography>
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      navigator.clipboard.writeText(ticketIntegrationHookUrl);
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </StyledUrlWrapper>
              </>
            )}

            <DividerStyled />

            <HeaderContainer>
              <Header variant="subtitle2">
                {t('settings.integrations.form.gpsProviderLabel')}
              </Header>

              {gpsIntegrationStatus && (
                <IntegrationStatusIcon
                  integrationStatusModel={gpsIntegrationStatus}
                />
              )}
            </HeaderContainer>

            <SectionDescription>
              {t('settings.integrations.form.gpsProviderDescription')}
            </SectionDescription>

            <IntegrationServiceField
              fieldName={FormFields.GpsProvider}
              label={t('settings.integrations.form.gpsProviderLabel')}
              placeholder={t(
                'settings.integrations.form.gpsProviderPlaceholder',
              )}
              control={control}
              integrationServiceOptions={gpsProviderOptions}
              isIntegrationSelected={!!selectedGpsIntegration}
              integrationStatus={gpsIntegrationStatus?.status || null}
              onChangeConfirm={() => autoSaveField(FormFields.GpsProvider)}
              onChangeCancel={() => reset()}
              onClearIntegrationConfirm={onClearGpsProvider}
            />

            {selectedGpsIntegration?.settings.map(field => (
              <FormField
                isRequired={field.isRequired}
                key={field.name}
                fieldName={field.name}
                label={field.label}
                fieldError={errors[field.name as FormFields]}
                inputProps={{
                  type: field.inputType,
                }}
                placeholder={field.placeholder}
                requiredErrorMessage={t('common.form.error.emptyField')}
                onChange={() => autoSaveField(field.name)}
                control={control}
                rules={{
                  ...(field.inputType === 'url'
                    ? {
                        pattern: {
                          value: URL_VALIDATION_REGEX,
                          message: t('common.form.invalidUrlError'),
                        },
                      }
                    : {}),
                }}
              />
            ))}

            {selectedGpsIntegration?.instructions && (
              <InstructionBlock>
                <Typography fontWeight={600} variant="body1" pb="1rem">
                  {t('settings.integrations.instruction')}
                </Typography>
                <Typography variant="body1">
                  {selectedGpsIntegration?.instructions as string}
                </Typography>
              </InstructionBlock>
            )}

            {selectedGpsIntegration && (
              <ConnectionButtonWrapper>
                <ConnectionButton
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={onTestGpsConnectionClick}
                  loading={testGpsConnectionPending}
                  disabled={!isValid}
                >
                  <SignalCellularAltOutlinedIcon />
                  {t('settings.integrations.form.testConnection')}
                </ConnectionButton>
                <ConnectionButton
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={onSyncTrucksClick}
                  loading={syncTrucksPending}
                  disabled={!isValid}
                >
                  <LocalShippingOutlinedIcon />
                  {t('settings.integrations.form.syncTrucks')}
                </ConnectionButton>
                <SyncTrucksPopup
                  isOpen={syncTrucksPopupOpen}
                  onClose={() => setSyncTrucksPopupOpen(false)}
                  providerId={providerId}
                />
              </ConnectionButtonWrapper>
            )}
          </StyledForm>
        </FormProvider>
      </Inner>
    </Wrapper>
  );
};

type IntegrationSettingsTabProps = {
  contractorId?: string;
};

export const IntegrationSettingsTab: React.FC<IntegrationSettingsTabProps> = ({
  contractorId,
}) => {
  const { fetchProviderSettings } = useApp();
  const handleApiResult = useHandleApiResult();
  const userProviderId = useSelector(appSelectors.providerId);
  const providerId = contractorId || userProviderId;
  const [isLoaded, setIsLoaded] = useState(false);

  const [formData, setFormData] = useState<Partial<FormData>>({});

  useEffect(() => {
    handleApiResult<APIProviderSettings[]>(
      () => fetchProviderSettings(providerId),
      ({ result }) => {
        setFormData(state => ({
          ...state,
          ...Object.keys(formDefaultValues).reduce(
            (obj, fieldName) => ({
              ...obj,
              [fieldName]:
                result.payload.find(it => it.key === fieldName)?.value ||
                formDefaultValues[fieldName as FormFields],
            }),
            {},
          ),
        }));

        setIsLoaded(true);
      },
    );
  }, [fetchProviderSettings, handleApiResult, providerId]);

  return isLoaded ? (
    <IntegrationSettingsForm data={formData} providerId={providerId} />
  ) : (
    <CircularProgress />
  );
};
